import { Button, useSnackbar } from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { useHistory, useParams } from "react-router-dom";
import { createDevelopmentProductOfferingUrl, createProjectProductOfferingUrl } from "src/RouteUrls";
import { PageHeader } from "src/routes/layout/PageHeader";
import { useReviewStep_SaveProductOfferingMutation } from "src/routes/libraries/product-offerings/add-product-offering/components/5.ReviewStep.generated";
import {
  EditProductOfferingFragment,
  useEditProductOfferingPageQuery,
} from "src/routes/libraries/product-offerings/edit-product-offering/EditProductOfferingPage.generated";
import { ProductOfferingParams } from "src/routes/routesDef";
import { queryResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { formConfig } from "../add-product-offering/AddProductOfferingPage";
import { OptionsView } from "../add-product-offering/components/4.OptionsStep";

export function EditProductOfferingPage() {
  const { productOfferingId, rpavId } = useParams<ProductOfferingParams>();
  const query = useEditProductOfferingPageQuery({ variables: { id: productOfferingId, versionId: rpavId } });
  return queryResult(query, ({ productOffering }) => <EditProductOfferingView productOffering={productOffering} />);
}

export function EditProductOfferingView({ productOffering }: { productOffering: EditProductOfferingFragment }) {
  const [{ devId, projectId }] = useQueryParams({ devId: StringParam, projectId: StringParam });
  const readOnly = !(productOffering.canEdit.allowed && productOffering.canEditOptions?.allowed);
  const disabledReasons = [
    ...(productOffering.canEdit.disabledReasons?.map((r) => r.message) ?? []),
    ...(productOffering.canEditOptions?.disabledReasons?.map((r) => r.message) ?? []),
  ];
  const { productOfferingId, rpavId } = useParams<ProductOfferingParams>();
  const { triggerNotice } = useSnackbar();
  const history = useHistory();
  const [saveProductOffering] = useReviewStep_SaveProductOfferingMutation();
  const { options, optionGroups, baseConfig } = productOffering;
  const formState = useFormState({
    config: formConfig,
    init: {
      input: { options, optionGroups },
      map: ({ options, optionGroups }) => ({
        options: options.filter((rpo) => rpo.active),
        optionGroups: optionGroups.filter((rpog) => rpog.active),
      }),
    },
    readOnly,
  });

  return (
    <>
      <PageHeader
        title=""
        right={
          <>
            <Button
              size="lg"
              variant="quaternary"
              label={readOnly ? "Close" : "Cancel"}
              onClick={() => {
                if (devId) {
                  history.push(createDevelopmentProductOfferingUrl(devId, productOfferingId, rpavId));
                } else if (projectId) {
                  history.push(createProjectProductOfferingUrl(projectId, productOfferingId, rpavId));
                }
              }}
            />

            {!readOnly && (
              <Button
                size="lg"
                label="Save Options"
                onClick={async () => {
                  if (!formState) return;
                  const res = await saveProductOffering({
                    variables: {
                      input: {
                        id: productOffering.id,
                        options: formState.options.value,
                        optionGroups: formState.optionGroups.value,
                      },
                    },
                  });
                  if (res.data?.saveProductOffering.productOffering) {
                    const productOfferingId = res.data?.saveProductOffering.productOffering.id;
                    triggerNotice({
                      message: "Offering successfully saved! Procurement will review and start bidding",
                    });
                    // TODO: route to a `global offering detail page` when offering is created from global library
                    const versionId = res.data?.saveProductOffering.productOffering.version.id;
                    if (devId) {
                      history.push(createDevelopmentProductOfferingUrl(devId, productOfferingId, versionId));
                    } else if (projectId) {
                      history.push(createProjectProductOfferingUrl(projectId, productOfferingId, versionId));
                    }
                  }
                }}
              />
            )}
          </>
        }
      />
      <OptionsView
        formState={formState}
        readyPlanOptions={options}
        baseConfig={baseConfig}
        readOnly={readOnly}
        disabledReasons={disabledReasons}
      />
    </>
  );
}
